/**
 * Display helpers. Everything the API returns is UTC; everything here
 * renders in the *viewer's* local zone, with the absolute value available
 * on hover via `title` wherever a relative time is shown.
 */

const dateTime = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const dateOnly = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });
const timeOnly = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' });
const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

export const formatDateTime = (iso?: string | null) =>
  iso ? dateTime.format(new Date(iso)) : '—';

export const formatDate = (iso?: string | null) =>
  iso ? dateOnly.format(new Date(iso)) : '—';

export const formatTime = (iso?: string | null) =>
  iso ? timeOnly.format(new Date(iso)) : '—';

/** "3 Nov, 17:00 – 18:00" — one date, both ends of the slot. */
export function formatSlotRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameDay = start.toDateString() === end.toDateString();
  return sameDay
    ? `${dateOnly.format(start)}, ${timeOnly.format(start)} – ${timeOnly.format(end)}`
    : `${dateTime.format(start)} – ${dateTime.format(end)}`;
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600_000],
  ['month', 30 * 24 * 3600_000],
  ['week', 7 * 24 * 3600_000],
  ['day', 24 * 3600_000],
  ['hour', 3600_000],
  ['minute', 60_000],
];

/** "in 20 minutes", "3 days ago". Falls back to "just now" under a minute. */
export function formatRelative(iso?: string | null): string {
  if (!iso) return '—';
  const diff = new Date(iso).getTime() - Date.now();
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms) return relative.format(Math.round(diff / ms), unit);
  }
  return 'just now';
}

/** Minor units to a localised amount: 25000 + USD -> "$250.00". */
export function formatMoney(cents: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(cents / 100);
  } catch {
    // An unknown/invalid currency code must not blank out a whole table row.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export const formatNumber = (n: number) => new Intl.NumberFormat().format(n);

export const formatDuration = (startIso: string, endIso: string): string => {
  const minutes = Math.round(
    (new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000,
  );
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/** Initials for the avatar fallback: "Sara Mahmoud" -> "SM". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * A long mailbox shortened in the middle so a table row stays one line:
 * "plans.student.9ce0abbe-0383-…@example.com" keeps the readable start, the
 * distinguishing end and the whole domain. The full address goes in a title.
 */
export function shortEmail(email: string, max = 24): string {
  const at = email.lastIndexOf('@');
  if (at < 0 || at <= max) return email;
  const local = email.slice(0, at);
  return `${local.slice(0, max - 6)}…${local.slice(-4)}${email.slice(at)}`;
}

/** `user.deactivated` -> `User deactivated`, for the audit log. */
export function humanizeAction(action: string): string {
  const text = action.replace(/[._]/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const SESSION_STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No show',
};

/** Local-datetime input value (`YYYY-MM-DDTHH:mm`) from an ISO instant. */
export function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** The inverse — a local datetime-local value back to an ISO instant. */
export function fromLocalInput(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
